import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import PaginationWithState from "components/Pagination/PaginationWithState";

export default function PayBook({ type }) {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [PayBookData, setPayBookData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const showManagerApprove = type === "pending_by_manager";
  const showOwnerApprove = type === "pending_by_owner";

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /manufacturer/accounts/day-books?page=&limit=)
  const getPayBookData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
            ? query
            : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/accounts/day-books?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterByTab: type,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() },
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setPayBookData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            }),
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, searchTerm, pageLimit, filterBy.value, type],
  );

  // Delete PayBook
  const deletePayBookData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(
        "/manufacturer/accounts/day-book/" + id,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // refresh current page
        await getPayBookData();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Adds Button Function
  const handlePayBookData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/accounts/day-book",
        formData,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // after add refresh current page (or go to first page)
        setFormData({});
        setShowAddModal(false);
        // Refresh list
        setPageNumber(1);
        getPayBookData({ page: 1 });
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const handleUpdatePayBookData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/manufacturer/accounts/day-book",
        formData,
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        setIsUpdateMode(false);
        setShowAddModal(false);
        // refresh list
        getPayBookData();
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const approveByManager = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/manufacturer/accounts/day-book",
        { id, approvedByManager: true },
        { headers: authHeader() },
      );
      if (data?.success) {
        await getPayBookData();
        dispatch(
          setNotification({
            message: data?.message || "Approved by manager",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to approve",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const approveByOwner = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/manufacturer/accounts/day-book",
        { id, approvedByOwner: true },
        { headers: authHeader() },
      );
      if (data?.success) {
        await getPayBookData();
        dispatch(
          setNotification({
            message: data?.message || "Approved by owner",
            type: "success",
          }),
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to approve",
          type: "error",
        }),
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    try {
      const { data } = await API.post(
        "/manufacturer/accounts/day-book-sort-up",
        { PayBookDataId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getPayBookData();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    try {
      const { data } = await API.post(
        "/manufacturer/accounts/day-book-sort-down",
        { PayBookDataId: id },
        { headers: authHeader() },
      );
      if (data.success) {
        await getPayBookData();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getPayBookData(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber },
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy, type]);

  /*   Table functions
   ********************************************* */
  let filters = [
    { value: "type", name: "Type" },
    { value: "desc", name: "Description" },
    { value: "debit", name: "Debit" },
    { value: "credit", name: "Credit" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "ID", value: "dayBookId" },
    { name: "Date", value: "date" },
    { name: "Type", value: "type" },
    { name: "Description", value: "desc" },
    { name: "Debit", value: "debit" },
    { name: "Credit", value: "credit" },
    { name: "Manager Approved", value: "approvedByManager" },
    { name: "Owner Approved", value: "approvedByOwner" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">{item.dayBookId}</td>
      <td className="align-middle py-1">
        {item.date ? new Date(item.date).toLocaleDateString() : ""}
      </td>
      <td className="align-middle py-1">{item.type}</td>
      <td className="align-middle py-1">{item.desc}</td>
      <td className="align-middle py-1">{item.debit}</td>
      <td className="align-middle py-1">{item.credit}</td>
      <td className="align-middle py-1">
        <span
          className={`badge badge-lg text-uppercase ${
            item.approvedByManager ? "bg-success" : "bg-danger"
          }`}
        >
          {item.approvedByManager ? "Yes" : "No"}
        </span>
      </td>
      <td className="align-middle py-1">
        <span
          className={`badge badge-lg text-uppercase ${
            item.approvedByOwner ? "bg-success" : "bg-danger"
          }`}
        >
          {item.approvedByOwner ? "Yes" : "No"}
        </span>
      </td>
      <td className="align-middle py-1 text-end table-action">
        {showManagerApprove && (
          <button
            disabled={loading || item.approvedByManager}
            onClick={() => approveByManager(item.id)}
            className="btn btn-sm btn-success px-1"
          >
            Approve
          </button>
        )}
        {showOwnerApprove && (
          <button
            disabled={loading || item.approvedByOwner}
            onClick={() => approveByOwner(item.id)}
            className="btn btn-sm btn-success px-1"
          >
            Approve
          </button>
        )}
        <button
          onClick={() => {
            sortUp(item.dayBookId);
          }}
          disabled={item.sortId === PayBookData?.length} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-up p-0"></i>
        </button>
        <button
          onClick={() => {
            sortDown(item.dayBookId);
          }}
          disabled={item.sortId === 1} // assuming sort by sortId Desc
          className="btn btn-sm px-1 border-0"
        >
          <i className="mdi mdi-chevron-double-down"></i>
        </button>
        <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>

        <DeleteModalButton
          btnClass={"btn btn-sm  px-1"}
          deleteFunc={deletePayBookData}
          deleteParams={item.id}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="overflow-y-auto pe-2" style={{ height: "100vh" }}>
      <h2 className="mb-3">Day Book</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getPayBookData({ query: searchTerm, page: 1 });
              } else {
                getPayBookData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by type, description, debit, credit..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getPayBookData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          <div className="">
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={PayBookData}
            disableSorting={true}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/*   Add Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>{isUpdateMode ? "Update Entry" : "Add Entry"}</h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={
              isUpdateMode ? handleUpdatePayBookData : handlePayBookData
            }
            className=""
          >
            <div className="mb-1">
              <label className="">Date</label>
              <div className="">
                <input
                  type="date"
                  autoFocus
                  value={formData?.date ? formData.date.slice(0, 10) : ""}
                  onChange={(e) => {
                    setFormData({ ...formData, date: e.target.value });
                  }}
                  className="form-control"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Type</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.type || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, type: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Type"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Description</label>
              <div className="">
                <textarea
                  value={formData?.desc || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, desc: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Description"
                  rows={3}
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Debit</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.debit || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, debit: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Debit"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Credit</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.credit || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, credit: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Credit"
                />
              </div>
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
